import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Users, Plus, Search, Edit, Trash2, Mail, Phone, MapPin, X, Eye } from 'lucide-react';
import { useCompany } from '../context/CompanyContext';
import { formatRelativeTime, cn } from '../utils/formatters';
import { CardSkeleton } from '../components/ui/LoadingScreen';
import { input, label, btnGhost, modalOverlay, modalContent, modalHeader, modalBody, modalFooter, formGroup } from '../styles/components';
import type { Client } from '../types';

type ClientFormState = {
  name: string;
  fiscalName: string;
  cif: string;
  address: string;
  phone: string;
  email: string;
  notes: string;
};

const emptyForm: ClientFormState = {
  name: '',
  fiscalName: '',
  cif: '',
  address: '',
  phone: '',
  email: '',
  notes: '',
};

export function ClientsPage() {
  const { clients, createClient, updateClient, deleteClient, isLoading } = useCompany();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form, setForm] = useState<ClientFormState>(emptyForm);
  const [editingClient, setEditingClient] = useState<Client | null>(null);

  const filteredClients = clients.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.email?.toLowerCase().includes(search.toLowerCase()) ||
    c.phone?.includes(search)
  );

  const openCreate = () => {
    setEditingClient(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (client: Client) => {
    setEditingClient(client);
    setForm({
      name: client.name,
      fiscalName: client.fiscalName || '',
      cif: client.cif || '',
      address: client.address,
      phone: client.phone || '',
      email: client.email || '',
      notes: client.notes || '',
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingClient(null);
    setForm(emptyForm);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      if (editingClient) {
        await updateClient(editingClient.id, form);
      } else {
        await createClient(form);
      }
      closeModal();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (confirm('¿Eliminar este cliente? Esta acción no se puede deshacer.')) {
      await deleteClient(id);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold">Clientes</h1>
        <div className="grid gap-4">
          {[1, 2, 3].map(i => <CardSkeleton key={i} />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="space-y-1">
          <span className="b2b-eyebrow">Cartera</span>
          <h1 className="text-3xl font-extrabold tracking-tight">Clientes</h1>
          <p className="b2b-muted">Gestiona tu cartera de clientes</p>
        </div>
        <button onClick={openCreate} className="b2b-btn b2b-btn--primary">
          <Plus className="w-4 h-4" /> Nuevo Cliente
        </button>
      </div>

      <div className="b2b-glass flex flex-col sm:flex-row gap-3 p-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary-400" />
          <input
            type="text"
            placeholder="Buscar por nombre, email, teléfono..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className={cn(input, 'pl-10')}
          />
        </div>
      </div>

      <div className="b2b-panel">
        <div className="b2b-panel__head">
          <h2 className="text-lg font-semibold">Listado</h2>
          <span className="text-sm b2b-muted">{filteredClients.length} cliente{filteredClients.length === 1 ? '' : 's'}</span>
        </div>

        {filteredClients.length === 0 ? (
          <div className="p-12 text-center">
            <Users className="w-16 h-16 mx-auto mb-4 text-secondary-300 dark:text-secondary-600" />
            <h3 className="text-lg font-medium mb-1">No hay clientes</h3>
            <p className="b2b-muted mb-4">Empieza añadiendo tu primer cliente</p>
            <button onClick={openCreate} className="b2b-btn b2b-btn--primary">
              <Plus className="w-4 h-4" /> Añadir cliente
            </button>
          </div>
        ) : (
          <>
            <div className="md:hidden divide-y divide-[var(--b2b-border)]">
              {filteredClients.map(client => (
                <div key={client.id} className="p-4 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <Link to={`/clients/${client.id}`} className="font-semibold text-[color:var(--b2b-accent-strong)]">
                        {client.name}
                      </Link>
                      {client.fiscalName && client.fiscalName !== client.name && (
                        <p className="text-xs b2b-muted truncate">{client.fiscalName}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={() => navigate(`/clients/${client.id}`)}
                        className="p-2 rounded-lg text-secondary-500 hover:bg-[var(--b2b-slate-100)] dark:hover:bg-[var(--b2b-slate-700)] transition-colors"
                        aria-label="Ver cliente"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => openEdit(client)}
                        className="p-2 rounded-lg text-secondary-500 hover:bg-[var(--b2b-slate-100)] dark:hover:bg-[var(--b2b-slate-700)] transition-colors"
                        aria-label="Editar cliente"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(client.id)}
                        className="p-2 rounded-lg text-danger-500 hover:bg-danger-50 dark:hover:bg-danger-900/20 transition-colors"
                        aria-label="Eliminar cliente"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  <div className="space-y-1 text-sm b2b-muted">
                    {client.email && (
                      <div className="flex items-center gap-2 truncate"><Mail className="w-3.5 h-3.5 shrink-0" /><span className="truncate">{client.email}</span></div>
                    )}
                    {client.phone && (
                      <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 shrink-0" /><span>{client.phone}</span></div>
                    )}
                    {client.address && (
                      <div className="flex items-start gap-2"><MapPin className="w-3.5 h-3.5 shrink-0 mt-0.5" /><span>{client.address}</span></div>
                    )}
                  </div>

                  <div className="flex items-center justify-between gap-3 pt-1">
                    <span className="b2b-pill">{client.cif || 'Sin CIF'}</span>
                    <span className="text-xs b2b-muted">{formatRelativeTime(client.createdAt)}</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider b2b-muted">
                    <th className="px-6 py-3 font-semibold">Cliente</th>
                    <th className="px-6 py-3 font-semibold">Contacto</th>
                    <th className="px-6 py-3 font-semibold">Dirección</th>
                    <th className="px-6 py-3 font-semibold">CIF/NIF</th>
                    <th className="px-6 py-3 font-semibold">Creado</th>
                    <th className="px-6 py-3 font-semibold text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredClients.map(client => (
                    <tr key={client.id} className="border-t border-[var(--b2b-border)] hover:bg-[var(--b2b-surface-muted)] transition-colors">
                      <td className="px-6 py-3">
                        <Link to={`/clients/${client.id}`} className="font-medium text-[color:var(--b2b-accent-strong)]">
                          {client.name}
                        </Link>
                        {client.fiscalName && client.fiscalName !== client.name && (
                          <p className="text-xs b2b-muted">{client.fiscalName}</p>
                        )}
                      </td>
                      <td className="px-6 py-3">
                        <div className="space-y-1 text-sm b2b-muted">
                          {client.email && (
                            <div className="flex items-center gap-1"><Mail className="w-3 h-3" /><span>{client.email}</span></div>
                          )}
                          {client.phone && (
                            <div className="flex items-center gap-1"><Phone className="w-3 h-3" /><span>{client.phone}</span></div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-3">
                        <span className="inline-flex items-center gap-1 text-sm b2b-muted">
                          <MapPin className="w-4 h-4" />{client.address}
                        </span>
                      </td>
                      <td className="px-6 py-3">
                        <span className="b2b-pill">{client.cif || '—'}</span>
                      </td>
                      <td className="px-6 py-3 text-sm b2b-muted">{formatRelativeTime(client.createdAt)}</td>
                      <td className="px-6 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => navigate(`/clients/${client.id}`)}
                            className="p-2 rounded-lg text-secondary-500 hover:bg-[var(--b2b-slate-100)] dark:hover:bg-[var(--b2b-slate-700)] transition-colors"
                            aria-label="Ver cliente"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openEdit(client)}
                            className="p-2 rounded-lg text-secondary-500 hover:bg-[var(--b2b-slate-100)] dark:hover:bg-[var(--b2b-slate-700)] transition-colors"
                            aria-label="Editar cliente"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(client.id)}
                            className="p-2 rounded-lg text-danger-500 hover:bg-danger-50 dark:hover:bg-danger-900/20 transition-colors"
                            aria-label="Eliminar cliente"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {showModal && (
        <div className={modalOverlay} onClick={closeModal} role="dialog" aria-modal="true" aria-labelledby="client-modal-title">
          <div className={cn(modalContent, 'w-full max-w-2xl')} onClick={e => e.stopPropagation()}>
            <div className={modalHeader}>
              <h2 id="client-modal-title" className="text-lg font-semibold">
                {editingClient ? 'Editar cliente' : 'Nuevo cliente'}
              </h2>
              <button onClick={closeModal} className="p-1 rounded-lg text-secondary-500 hover:bg-[var(--b2b-slate-100)] dark:hover:bg-[var(--b2b-slate-700)]" aria-label="Cerrar">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form id="client-form" onSubmit={handleSubmit} className={cn(modalBody, 'space-y-4')} noValidate>
              <div className={formGroup}>
                <label htmlFor="name" className={label}>Nombre *</label>
                <input id="name" type="text" className={input} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
              </div>

              <div className={formGroup}>
                <label htmlFor="fiscalName" className={label}>Razón social</label>
                <input id="fiscalName" type="text" className={input} value={form.fiscalName} onChange={e => setForm({ ...form, fiscalName: e.target.value })} />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className={formGroup}>
                  <label htmlFor="cif" className={label}>CIF / NIF</label>
                  <input id="cif" type="text" className={input} value={form.cif} onChange={e => setForm({ ...form, cif: e.target.value })} />
                </div>
                <div className={formGroup}>
                  <label htmlFor="phone" className={label}>Teléfono</label>
                  <input id="phone" type="tel" className={input} value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
                </div>
              </div>

              <div className={formGroup}>
                <label htmlFor="email" className={label}>Email</label>
                <input id="email" type="email" className={input} value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
              </div>

              <div className={formGroup}>
                <label htmlFor="address" className={label}>Dirección *</label>
                <textarea id="address" className={cn(input, 'min-h-[70px]')} value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} required />
              </div>

              <div className={formGroup}>
                <label htmlFor="notes" className={label}>Notas</label>
                <textarea id="notes" className={cn(input, 'min-h-[70px]')} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
              </div>
            </form>
            <div className={modalFooter}>
              <button type="button" onClick={closeModal} className={btnGhost}>Cancelar</button>
              <button type="submit" form="client-form" className="b2b-btn b2b-btn--primary disabled:opacity-50" disabled={isSubmitting}>
                {isSubmitting ? 'Guardando...' : (editingClient ? 'Actualizar' : 'Crear')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
